import { Button, HStack, Stack, StackProps, Text } from '@chakra-ui/react'
import BuyNowButton from 'components/buttons/BuyNowButton'
import ImageBox from 'components/common/ImageBox'
import { applyDiscount } from 'components/modules/patientArea/common/DoctorChoice/ChooseMediumStep'
import { BulletCheck } from 'components/svg/BulletCheck'
import NewPill from 'components/svg/NewPill'
import { Body1, Body3, H3 } from 'components/typography'
import { MAIS_FERT_VARIANTS, MAIS_HAIR_VARIANTS, MAIS_LIB_VARIANTS } from 'constants/products/menPower'
import { useIsSmartfitClient } from 'hooks/helpers/useIsSmartfitClient'
import { isEqual } from 'lodash'
import { useRouter } from 'next/router'
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import {
  MEN_POWER_CHECKOUT_INITIAL_STEP,
  resetCheckoutMenPowerState,
  setActiveVariantId
} from 'redux/slices/checkout-men-power-slice'
import { useAppDispatch, useAppSelector } from 'redux/store'

const items = ['Entregamos em todo o território nacional.', 'Pronta entrega!']

export interface infoItemsProps {
  label: string
  price: number
  prevPrice?: number
  variantId?: string | number
  amount?: number
}

interface BuyNowSectionProps {
  productImage?: string
  title?: ReactNode
  infoItems?: infoItemsProps[]
}

const BuyNowSection: FC<BuyNowSectionProps> = ({
  productImage = '/landings/products/sexual_health/mais-hair-hero.png',
  title,
  infoItems
}) => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const isSmartfit = useIsSmartfitClient()
  const { activeVariantId } = useAppSelector((state) => state.checkoutMenPower)
  const [activeItem, setActiveItem] = useState<infoItemsProps | null>(null)

  useEffect(() => {
    if (router.pathname === '/produtos/mais-fert') {
      dispatch(setActiveVariantId(MAIS_FERT_VARIANTS[0].variantId))
    } else if (router.pathname === '/produtos/mais-hair') {
      dispatch(setActiveVariantId(MAIS_HAIR_VARIANTS[0].variantId))
    } else if (router.pathname === '/produtos/mais-lib') {
      dispatch(setActiveVariantId(MAIS_LIB_VARIANTS[0].variantId))
    }
  }, [router.pathname, dispatch])

  const handleVariantSelect = useCallback(
    (variantId: string | number) => {
      dispatch(setActiveVariantId(variantId))
    },
    [dispatch]
  )

  const handleNavigateCheckout = useCallback(() => {
    dispatch(resetCheckoutMenPowerState())
    dispatch(setActiveVariantId(activeVariantId))
    router.push(MEN_POWER_CHECKOUT_INITIAL_STEP)
  }, [dispatch, router, activeVariantId])

  const handleOnClickButton = (item: infoItemsProps) => {
    setActiveItem(item)
    handleVariantSelect(item?.variantId ?? '')
  }

  const description = useMemo(() => {
    switch (router.pathname) {
      case '/produtos/mais-hair':
        return 'Tome 1 cápsula por dia.'
      default:
        return 'Tome 2 cápsulas por dia.'
    }
  }, [router])

  return (
    <>
      <Stack direction={{ base: 'column', lg: 'row' }} spacing={0}>
        <Stack
          flex={1}
          gap={4}
          minH={{
            base: 436,
            lg: 786
          }}
          bg="dandelion"
          pos="relative"
        >
          <ImageBox
            h={{
              base: 350,
              lg: 'full'
            }}
            w="full"
            mt="auto"
            mx="auto"
            maxW={500}
            alt={'imagem ilustrativa do produto'}
            src={productImage}
            imgProps={{
              objectFit: 'contain',
              objectPosition: {
                base: 'bottom right',
                lg: 'bottom center'
              }
            }}
          />

          <Stack
            pos="absolute"
            top={{
              base: '24px',
              lg: '64px'
            }}
            left={{
              base: '24px',
              lg: '64px'
            }}
            spacing={4}
          >
            <Tag label={<NewPill />} description="Suplementação diária" />
            <Tag
              label={
                <Text fontWeight={800} fontSize="xl">
                  01
                </Text>
              }
              description={description}
            />
          </Stack>
        </Stack>

        <Stack
          flex={1}
          bg="grayscale.20"
          px={{
            base: '24px',
            lg: '56px'
          }}
          py={{
            base: '32px',
            lg: '64px'
          }}
          justifyContent="space-between"
          spacing={8}
        >
          <Stack spacing={8}>
            {title && <H3 color="darkchoc">{title}</H3>}

            <Stack spacing={4}>
              {items.map((item) => (
                <HStack key={item}>
                  <BulletCheck />
                  <Body1>{item}</Body1>
                </HStack>
              ))}
            </Stack>
          </Stack>

          <Stack spacing={4}>
            <Body1>Selecione a duração da suplementação:</Body1>
            <Stack>
              {infoItems?.map((item) => {
                const isActive = isEqual(activeItem, item)

                return (
                  <CustomButtonVariant
                    key={item.variantId}
                    item={item}
                    isActive={isActive}
                    isSmartfit={isSmartfit}
                    handleOnClickButton={handleOnClickButton}
                  />
                )
              })}

              <BuyNowButton w="fit-content" size="lg" fontSize="xl" shadow="none" onClick={handleNavigateCheckout}>
                Comece agora seu tratamento
              </BuyNowButton>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </>
  )
}

export default BuyNowSection

const Tag: FC<
  {
    label?: ReactNode
    description: string
  } & StackProps
> = ({ label, description, ...props }) => {
  return (
    <Stack p={4} borderRadius="20px" bg="alabaster" maxW={136} {...props}>
      {label}
      <Body3 fontWeight={800}>{description}</Body3>
    </Stack>
  )
}

interface CustomButtonProps {
  item: infoItemsProps
  isActive: boolean
  isSmartfit: boolean
  handleOnClickButton: (item: infoItemsProps) => void
}

export const CustomButtonVariant: FC<CustomButtonProps> = ({ item, isActive, isSmartfit, handleOnClickButton }) => {
  return (
    <Button
      key={item.label}
      bg={isActive ? 'phthalo' : 'white'}
      color={isActive ? 'alabaster' : 'phthalo'}
      onClick={() => handleOnClickButton(item)}
      _focus={{}}
      _hover={{}}
      w="fit-content"
      size="lg"
      shadow="none"
      gap={4}
    >
      <Text fontSize="xl">{item.label}</Text>
      <Text fontSize="xl">|</Text>
      {item.prevPrice && (
        <Text
          fontSize="xl"
          textDecoration="line-through"
          textDecorationColor="dandelion"
          textDecorationThickness="4px"
          color="#C7CBC4"
        >
          R${item.prevPrice}
        </Text>
      )}
      <Text fontSize="xl" fontWeight="bold">
        R${isSmartfit ? applyDiscount(item.price, 10) : item.price}
      </Text>
    </Button>
  )
}
